/** Replaces add-stream-picker/add-stream-picker.tsx on parked/data-viewer-followup-v2. */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ListDataStreams200Item } from "@/api/orval/generated/schemas";
import { Button } from "@/components/ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "@/components/ui/popover";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS, iconRegistry } from "@/icons";
import { StreamPickerPanel } from "./stream-picker-panel";
import { usePickerCatalog } from "./use-picker-catalog";

const AddStreamIcon = iconRegistry[ICON_KEYS.ADD_DATA_STREAM];

/** Only the list scrolls; zeroing the padding var stops a width jump while the popup opens. */
export const STREAM_PICKER_POPUP_CLASS =
    "[&_[data-slot=popover-viewport]]:overflow-hidden [&_[data-slot=popover-viewport]]:py-0 [&_[data-slot=popover-viewport]]:[--viewport-inline-padding:0px]";

export function StreamPickerTrigger({ label }: { label: string }) {
    return (
        <PopoverTrigger render={<Button aria-label={label} size={"icon"} tooltip={label} variant={"outline"} />}>
            <AddStreamIcon />
        </PopoverTrigger>
    );
}

export function StreamPicker({
    zoneId,
    plottedStreams,
    addedIds,
    isPlottable,
    onAdd,
    onRemove,
}: {
    zoneId: string;
    plottedStreams: readonly ListDataStreams200Item[];
    addedIds: readonly number[];
    /** From the spec hook, so the picker offers only what the viewer would draw. */
    isPlottable: (stream: ListDataStreams200Item) => boolean;
    onAdd: (streamId: number) => void;
    onRemove: (streamId: number) => void;
}) {
    const { t } = useTranslation(NAMESPACES.FEATURES.DATA_VIEWER, { keyPrefix: "stream-picker" });
    // Not lifted: the card and the expand dialog each mount a picker, and only one should open.
    const [open, setOpen] = useState(false);
    const { plotted, catalog, status } = usePickerCatalog({
        enabled: open,
        zoneId,
        plottedStreams,
        isPlottable,
    });
    const added = useMemo(() => new Set(addedIds), [addedIds]);
    const label = t("button-label");

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <StreamPickerTrigger label={label} />
            <PopoverPopup align={"start"} className={STREAM_PICKER_POPUP_CLASS} side={"bottom"}>
                <StreamPickerPanel
                    addedIds={added}
                    catalog={catalog}
                    onAdd={onAdd}
                    onRemove={onRemove}
                    plotted={plotted}
                    status={status}
                />
            </PopoverPopup>
        </Popover>
    );
}
